import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Form, Input, Select, Button, DatePicker, message } from 'antd'
import dayjs from 'dayjs'
import { createBroadcastTaskApi } from '@/service/broadcast/api/createBroadcastTask'
import { getChannelListApi } from '@/service/channel/api/getChannelList'
import { getBroadcastUserListApi } from '@/service/broadcast/api/getBroadcastUserList'
import type { ChannelItemRes } from '@/service/channel/types/getChannelList'
import type { BroadcastUserItem } from '@/service/broadcast/types/getBroadcastUserList'
import AddUser from './addUser'

interface CreateTaskProps {
    onSuccess?: () => void
    onCancel?: () => void
}

const CreateTask = ({ onSuccess, onCancel }: CreateTaskProps) => {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [channelList, setChannelList] = useState<ChannelItemRes[]>([])
    const [userList, setUserList] = useState<BroadcastUserItem[]>([])
    const [userLoading, setUserLoading] = useState(false)
    const companyName = useSelector((state: any) => state.user.userInfo?.company_name)

    useEffect(() => {
        if (!companyName) return
        getChannelListApi({ company: companyName, channel: 'Telegram' }).then(res => {
            if (res.code === 0) {
                setChannelList(res.data.list)
            }
        })
    }, [companyName])

    const handleChannelChange = (channelId: string) => {
        form.setFieldValue('user_id', [])
        setUserList([])
        if (!channelId) return
        setUserLoading(true)
        getBroadcastUserListApi({
            channel: 'Telegram',
            channel_id: channelId,
            limit: 1000,
            page: 1,
        }).then(res => {
            if (res.code === 0) {
                setUserList(res.data.list)
            }
        }).finally(() => setUserLoading(false))
    }

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields()
            setLoading(true)
            const res = await createBroadcastTaskApi({
                company: companyName || '',
                channel: 'Telegram',
                channel_id: values.channel_id,
                name: values.name,
                content: values.content,
                user_id: values.user_id,
                schedule_time: values.schedule_time
                    ? dayjs(values.schedule_time).unix()
                    : 0,
            })
            if (res.code === 0) {
                message.success('Task created successfully')
                onSuccess?.()
            } else {
                message.error(res.message || 'Failed to create task')
            }
        } catch {
            // validation failed
        } finally {
            setLoading(false)
        }
    }

    return (
        <Form name="broadcast-create" form={form} layout="vertical">
            <Form.Item
                name="name"
                label="Task Name"
                rules={[{ required: true, message: 'Please input task name' }]}
            >
                <Input placeholder="Enter task name" autoComplete="off" allowClear />
            </Form.Item>

            <Form.Item
                name="channel_id"
                label="Channel ID"
                rules={[{ required: true, message: 'Please select channel' }]}
            >
                <Select
                    placeholder="Select channel"
                    allowClear
                    onChange={handleChannelChange}
                    options={channelList.map(item => ({
                        label: `${item.channel_name} (${item.channel_id})`,
                        value: item.channel_id,
                    }))}
                />
            </Form.Item>

            <Form.Item
                name="user_id"
                label="User ID"
                rules={[{ required: true, type: 'array', min: 1, message: 'Please select users' }]}
            >
                <AddUser userList={userList} loading={userLoading} />
            </Form.Item>

            <Form.Item
                name="content"
                label="Content"
                rules={[{ required: true, message: 'Please input content' }]}
            >
                <Input.TextArea
                    placeholder="Enter broadcast content"
                    rows={4}
                />
            </Form.Item>

            <Form.Item
                name="schedule_time"
                label="Schedule Time"
                rules={[{ required: true, message: 'Please select schedule time' }]}
            >
                <DatePicker
                    showTime={{ hideDisabledOptions: true }}
                    allowClear
                    popupClassName="create-task-picker-dropdown"
                    format="YYYY-MM-DD HH:mm:ss"
                    style={{ width: '100%' }}
                    disabledDate={(current) => {
                        if (!current) return false
                        const today = dayjs().startOf('day')
                        const maxDay = dayjs().add(6, 'day').endOf('day')
                        return current < today || current > maxDay
                    }}
                    disabledTime={(current) => {
                        if (!current || !current.isSame(dayjs(), 'day')) return {}
                        const now = dayjs()
                        return {
                            disabledHours: () =>
                                Array.from({ length: now.hour() }, (_, i) => i),
                            disabledMinutes: (selectedHour) =>
                                selectedHour === now.hour()
                                    ? Array.from({ length: now.minute() }, (_, i) => i)
                                    : [],
                            disabledSeconds: (selectedHour, selectedMinute) =>
                                selectedHour === now.hour() && selectedMinute === now.minute()
                                    ? Array.from({ length: now.second() }, (_, i) => i)
                                    : [],
                        }
                    }}
                />
            </Form.Item>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
                <Button
                    onClick={onCancel}
                    style={{
                        color: '#5793FA',
                        background: '#121417',
                        border: '1px solid #5793FA',
                        borderRadius: 8,
                        height: 32,
                        width: 120,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    type="primary"
                    onClick={handleSubmit}
                    loading={loading}
                    style={{
                        color: '#25D1F7',
                        background: 'rgba(37, 209, 247, 0.08)',
                        border: '1px solid #25D1F7',
                        borderRadius: 8,
                        fontWeight: 600,
                        height: 32,
                        width: 120,
                    }}
                >
                    Create
                </Button>
            </div>
        </Form>
    )
}

export default CreateTask